import { useState, useCallback, useEffect, useRef } from "react";

import { DirectUpload } from "@rails/activestorage";
import { findBy } from "neetocist";
import { assoc } from "ramda";
import { useDropzone } from "react-dropzone";

import { DIRECT_UPLOAD_URL } from "../constants";
import { convertMbToBytes, renderErrorMessage } from "../utils";

const useFileUpload = ({
  disabled,
  accept,
  multiple,
  maxFileSize,
  minFileSize,
  files,
  onChange,
  setError,
  setTouched,
  hasFileSizeLimit,
}) => {
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadError, setUploadError] = useState("");

  const filesRef = useRef(files);
  filesRef.current = files;

  const cancel = useCallback(
    index => () => {
      setUploadProgress(assoc(files[index].name, null));
      const newFiles = files?.filter((_, idx) => idx !== index);
      onChange(newFiles);
    },
    [files]
  );

  useEffect(() => {
    uploadError && setError(uploadError);
  }, [uploadError]);

  const onDropAccepted = async acceptedFiles => {
    setTouched(false);
    setUploadError("");
    const initialFiles = acceptedFiles.map(file => ({
      name: file.name,
      size: file.size,
    }));

    const updatedFiles = multiple
      ? [...(files || []), ...initialFiles]
      : initialFiles;
    onChange(updatedFiles);
    const newFiles = await new Promise(resolve => {
      const files = [];

      acceptedFiles.forEach(file => {
        setUploadProgress(assoc(file.name, 0));

        const upload = new DirectUpload(file, DIRECT_UPLOAD_URL, {
          directUploadWillStoreFileWithXHR: xhr => {
            xhr.upload.addEventListener("progress", event => {
              const percentComplete = Math.round(
                (event.loaded / event.total) * 100
              );
              setUploadProgress(assoc(file.name, percentComplete));
            });

            xhr.upload.addEventListener("load", () => {
              setUploadProgress(assoc(file.name, undefined));
            });
          },
        });

        upload.create((error, blob) => {
          if (error) {
            return setUploadError(error);
          }

          files.push({
            url: blob.blob_url,
            name: blob.filename,
            signedId: blob.signed_id,
          });

          const isUploadCompleted = multiple
            ? acceptedFiles.length === files.length
            : files.length === 1;

          if (isUploadCompleted) {
            return resolve(files);
          }

          return "";
        });
      });

      return files;
    });

    if (multiple) {
      const updatedFiles = filesRef.current.map(file => {
        const uploadedFile = findBy({ name: file.name }, newFiles);

        return { ...file, ...uploadedFile };
      });
      onChange(updatedFiles);
    } else {
      onChange(newFiles);
    }
  };

  const onDropRejected = fileRejections => {
    fileRejections.forEach(fileObject => {
      setTouched(true);
      if (fileObject.errors[0].code === "too-many-files") {
        setUploadError(renderErrorMessage(fileObject, maxFileSize, multiple));
      } else {
        setUploadError(renderErrorMessage(fileObject, maxFileSize, multiple));
      }
    });
  };

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      disabled,
      accept,
      multiple,
      minSize: convertMbToBytes(minFileSize),
      onDropAccepted,
      onDropRejected,
      ...(maxFileSize &&
        hasFileSizeLimit && { maxSize: convertMbToBytes(maxFileSize) }),
    });

  return {
    progress: uploadProgress,
    cancel,
    getRootProps,
    getInputProps,
    isDragActive,
    fileRejections,
  };
};

export default useFileUpload;
